"use client";

import { Button } from "@/components/ui/button";
import { api } from "@/convex/_generated/api";
import { normalizeOtpError } from "@/lib/auth/otp-error";
import { getDeviceInfo } from "@/lib/utils/device";
import { useAction } from "convex/react";
import { motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { toast } from "@/lib/toast";

interface EmailOTPVerificationProps {
  email: string;
  onBack: () => void;
  onSuccess: (token: string, user: unknown) => void;
  onError: (error: string) => void;
  testOtp?: string | null;
  sourceApp?: "web" | "food_creator" | "driver" | "mobile";
}

export function EmailOTPVerification({
  email,
  onBack: _onBack,
  onSuccess,
  onError,
  testOtp: initialTestOtp,
  sourceApp = "web",
}: EmailOTPVerificationProps) {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes
  const [error, setError] = useState("");
  const [testOtp, setTestOtp] = useState<string | null>(initialTestOtp || null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const verifyAndLogin = useAction(
    api.actions.users.customerEmailVerifyAndLogin,
  );
  const sendEmailOTP = useAction(api.actions.users.customerEmailSendOTP);

  // Countdown timer
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  // Auto-focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleOtpChange = (index: number, value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, otp.length);
    const newOtp = [...otp];

    if (digits.length > 1) {
      const startIndex = digits.length === otp.length ? 0 : index;
      digits.split("").forEach((digit, offset) => {
        const targetIndex = startIndex + offset;
        if (targetIndex < newOtp.length) {
          newOtp[targetIndex] = digit;
        }
      });
      setOtp(newOtp);
      setError("");
      inputRefs.current[Math.min(startIndex + digits.length - 1, newOtp.length - 1)]?.focus();
      return;
    }

    newOtp[index] = digits;
    setOtp(newOtp);

    if (digits) {
      setError("");

      // Auto-focus next input
      if (index < 5 && inputRefs.current[index + 1]) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (index: number, e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);

    if (pastedData) {
      handleOtpChange(index, pastedData);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpString = otp.join("");

    if (otpString.length !== 6) {
      setError("Please enter the complete 6-digit code");
      return;
    }

    setIsVerifying(true);
    setError("");

    try {
      const data = await verifyAndLogin({
        email,
        otp: otpString,
        sourceApp,
        userAgent:
          typeof window !== "undefined" ? navigator.userAgent : undefined,
        ...getDeviceInfo(),
      });

      if (data.success) {
        // Handle response structure: data.sessionToken and data.user
        const token = data.sessionToken;
        const user = data.user;

        if (token) {
          onSuccess(token, user);
        } else {
          setError("Authentication failed. Please try again.");
          setIsVerifying(false);
        }
      } else if (data.requires2FA) {
        // Handle 2FA if needed (not currently implemented in this modal but action supports it)
        setError(
          "2FA required - please complete on mobile or contact support.",
        );
        onError("2FA required - please complete on mobile or contact support.");
        setIsVerifying(false);
      } else {
        const normalizedError = normalizeOtpError(
          data.error,
          "Verification failed. Please check and try again.",
        );
        setError(normalizedError.message);
        onError(normalizedError.message);
        setIsVerifying(false);
      }
    } catch (err) {
      console.error("OTP verification error:", err);
      const normalizedError = normalizeOtpError(
        err,
        "Network error. Please check your connection and try again.",
      );
      setError(normalizedError.message);
      onError(normalizedError.message);
      setIsVerifying(false);
    }
  };

  const handleResendOTP = async () => {
    setIsResending(true);
    setError("");

    try {
      const data = await sendEmailOTP({ email, sourceApp });

      if (data.success) {
        // In development, show the test OTP
        if (data.testOtp) {
          setTestOtp(data.testOtp);
          console.log("🔐 Development OTP Code:", data.testOtp);
          toast.info("Development Mode", {
            description: `OTP Code: ${data.testOtp}`,
            duration: 10000,
          });
        }
        setTimeLeft(300); // Reset timer
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
        setError(""); // Clear any previous errors
        toast.success("Code Resent", {
          description: "A new verification code has been sent to your email.",
        });
      } else {
        const normalizedError = normalizeOtpError(
          data.error,
          "Failed to resend code",
        );
        setError(normalizedError.message);
        onError(normalizedError.message);
        toast.error("Failed to Resend Code", {
          description: normalizedError.message,
        });
      }
    } catch (err) {
      const normalizedError = normalizeOtpError(
        err,
        "Network error. Please try again.",
      );
      setError(normalizedError.message);
      onError(normalizedError.message);
    } finally {
      setIsResending(false);
    }
  };

  const otpString = otp.join("");
  const isComplete = otpString.length === 6;

  return (
    <motion.div
      initial={{ opacity: 0, x: 25 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -25 }}
      transition={{ duration: 0.5 }}
      className="w-full"
    >
      {/* Header */}
      <div className="mb-10 text-left">
        <h2 className="text-[36px] md:text-[40px] leading-[1.1] font-bold text-white mb-4 font-asgard tracking-[-1px]">
          Enter verification code
        </h2>
        <p className="text-[17px] md:text-[19px] leading-7 text-[#E5E7EB] opacity-80 font-satoshi max-w-[380px]">
          We sent a 6-digit verification code to{" "}
          <span className="text-white font-semibold">{email}</span>
        </p>

        {/* Development OTP Display */}
        {testOtp && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 p-4 bg-[#4ADE80]/10 border border-[#4ADE80]/30 rounded-xl max-w-[320px]"
          >
            <p className="text-sm text-[#4ADE80] font-satoshi">
              <strong>Dev Mode:</strong> Use code{" "}
              <code className="font-mono font-bold text-white ml-1">
                {testOtp}
              </code>
            </p>
          </motion.div>
        )}
      </div>

      {/* OTP Input */}
      <form onSubmit={handleVerifyOTP} className="space-y-10">
        <div className="flex gap-3 sm:gap-4 justify-start">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={(e) => handlePaste(index, e)}
              className="w-12 h-16 sm:w-14 sm:h-18 text-center text-2xl font-bold text-white bg-white/5 border border-white/10 rounded-xl focus:border-[#4ADE80] focus:ring-4 focus:ring-[#4ADE80]/10 font-mono outline-none transition-all"
              autoComplete={index === 0 ? "one-time-code" : "off"}
            />
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 text-red-400 text-base font-medium"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
            <span>{error}</span>
          </motion.div>
        )}

        {/* Timer & Links */}
        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-2 text-sm md:text-base text-[#E5E7EB] opacity-60">
            <span className="font-satoshi">
              {timeLeft > 0
                ? `Code expires in ${formatTime(timeLeft)}`
                : "The code has expired"}
            </span>
          </div>

          <div className="text-left">
            <button
              type="button"
              onClick={handleResendOTP}
              disabled={isResending || timeLeft > 240}
              className="text-base font-semibold text-[#4ADE80] hover:text-[#52EE8A] disabled:text-white/20 disabled:cursor-not-allowed transition-all font-satoshi flex items-center gap-1 group"
            >
              {isResending ? (
                <>
                  <div className="w-4 h-4 border-2 border-[#4ADE80] border-t-transparent rounded-full animate-spin mr-1" />
                  Sending...
                </>
              ) : (
                "Didn't get the code? Tap to resend"
              )}
            </button>
          </div>
        </div>

        {/* Verify Button */}
        <Button
          type="submit"
          disabled={!isComplete || isVerifying || timeLeft === 0}
          className="w-full max-w-[400px] h-16 bg-[#ff3b30] hover:bg-[#ff5e54] text-white font-bold text-xl rounded-2xl shadow-[0_8px_30px_rgb(255,59,48,0.2)] transition-all active:scale-[0.98] disabled:opacity-50 disabled:grayscale"
        >
          {isVerifying ? (
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 border-3 border-[#02120A] border-t-transparent rounded-full animate-spin" />
              <span>Verifying...</span>
            </div>
          ) : (
            "Verify and Continue"
          )}
        </Button>
      </form>
    </motion.div>
  );
}
